import React from 'react'

import { MainContainer } from '@core/Components/Containers'
import { ContentHeader } from '@core/Components/ContentHeader'
import { useBiometricsStatus } from '@mobi/account/Areas/Verification/Biometrics/Hooks/useBiometricsStatus'
import {
  BiometricsPageSubHeaderStyled,
  ContentBodyStyled,
  GeneralSettingStyled,
} from './BiometricsPage.styles'
import { BiometricsToggle } from '@mobi/account/Areas/Verification/Biometrics/Components/BiometricsToggle'
import { trackBiometricsToggle } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { keys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { sendToNative } from '@mobi/web-native-comms/web'
import styled from '@emotion/styled'
import { useCognitoUser } from '@core/Data/Account/useCognitoUser'
import { NoticeBox } from '@core/Components/NoticeBox'
import { useLogon } from '@core/Utils/hooks'

export const BiometricsPage = () => {
  const { accountNumber } = useLogon()
  return accountNumber ? <BiometricsPageContents accountNumber={accountNumber} /> : null
}

function BiometricsPageContents({ accountNumber }: { accountNumber: number }) {
  const testId = { 'data-tid-biometrics-page-container': '' }
  const { isBiometricsStatusReady, isBiometricsEnabled } = useBiometricsStatus()
  const isBiometricsToggleOn = isBiometricsStatusReady ? isBiometricsEnabled : false

  // Currently we cannot turn on Biometric if MFA is enabled - https://rwwa.atlassian.net/browse/MOBI-3823

  const { data: cognitoUser, isLoading: isMFAUserLoading } = useCognitoUser(accountNumber)
  const preferredFactor = cognitoUser?.preferredFactor ?? 'Unknown'
  const isMFAEnabled = preferredFactor !== 'None' && preferredFactor !== 'Unknown'
  const isBlockedByMFA = isMFAUserLoading || !cognitoUser || isMFAEnabled

  return (
    <MainContainer {...testId}>
      <ContentHeader title='Enable PIN/Biometrics' />
      <ContentBodyStyled>
        <GeneralSettingStyled>
          <BiometricsPageSubHeaderStyled>General Settings</BiometricsPageSubHeaderStyled>

          <BiometricsToggleWrapper>
            <BiometricsToggle
              toggleState={isBiometricsToggleOn}
              text='Use Face ID, Fingerprint or PIN to login'
              isDisabled={!isBiometricsStatusReady || (isBlockedByMFA && !isBiometricsToggleOn)}
              onToggle={() => {
                sendToNative('REQUEST_TOGGLE_BIOMETRICS')
                trackBiometricsToggle(keys.biometricsTogglePressed, {
                  isEnabled: !isBiometricsToggleOn,
                })
              }}
              toggleTitle='Enable/Disable'
            />
          </BiometricsToggleWrapper>

          {!isMFAUserLoading && isMFAEnabled && (
            <NoticeBox
              title=''
              noticeBoxType='Warning'
              hasBorder
              data-testid='mfa-enabled-notice-box'
            >
              Biometrics can only be enabled from the log-in page for your account
            </NoticeBox>
          )}
        </GeneralSettingStyled>
      </ContentBodyStyled>
    </MainContainer>
  )
}

const BiometricsToggleWrapper = styled.div({
  '> div': { justifyContent: 'space-between' },
})
